import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DatePipe} from '@angular/common';
import {CompanyService} from '@services/company.service';
import {CompanyDataService} from '@services/company-data.service';
import {ICompany} from '@models/company.interface';
import {ICompteAdministration, IPasswordUpdateRequest} from '@models/compteAdministration.interface';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  LucideAngularModule,
  Save,
  X,
} from '@shared/components/icons/google-icons';
import {NotificationService} from '@services/frontend/notification.service';

export interface DocumentStatus {
  name: string;
  type: string;
  source: string;
  available: boolean;
  id: string | null;
  date: string | null;
  credentialsConfigured: boolean;
  credentialSource: string;
  unavailableMessage?: string;
}

@Component({
  selector: 'app-company-attestations',
  imports: [DatePipe, LucideAngularModule],
  templateUrl: './company-attestations.html',
})
export class CompanyAttestations implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly companyData = inject(CompanyDataService);
  private readonly notificationService = inject(NotificationService);

  compteAdministration: WritableSignal<ICompteAdministration | null> = signal(null);
  company: WritableSignal<ICompany> = signal({} as ICompany);

  passwordForm: WritableSignal<IPasswordUpdateRequest> = signal({
    nomUtilisateurUrssaf: null,
    passwordUrssaf: null,
    typeCompteUrssaf: null,
    nomUtilisateurImpotsGouv: null,
    passwordImpotsGouv: null,
    nomUtilisateurProvigis: null,
    passwordProvigis: null,
    nomUtilisateurIdenum: null,
    passwordIdenum: null,
    passwordProbtp: null,
    nomUtilisateurCibtp: null,
    passwordCibtp: null,
  });

  showPasswordUrssaf = signal(false);
  showPasswordImpots = signal(false);
  showPasswordProvigis = signal(false);
  showPasswordIdenum = signal(false);
  savingPasswords = signal(false);

  documentStatuses: WritableSignal<DocumentStatus[]> = signal([]);

  private currentSiret = '';

  protected readonly KeyRound = KeyRound;
  protected readonly Eye = Eye;
  protected readonly EyeOff = EyeOff;
  protected readonly Save = Save;
  protected readonly X = X;
  protected readonly FileText = FileText;
  protected readonly Download = Download;
  protected readonly CheckCircle2 = CheckCircle2;
  protected readonly Clock = Clock;
  protected readonly AlertCircle = AlertCircle;

  ngOnInit() {
    this.currentSiret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.reload();
  }

  private reload() {
    this.companyData.load(this.currentSiret).subscribe(compte => this.applyCompte(compte));
  }

  private applyCompte(compte: ICompteAdministration) {
    this.compteAdministration.set(compte);
    this.company.set(compte.company);
    this.passwordForm.set({
      nomUtilisateurUrssaf: compte.nomUtilisateurUrssaf,
      passwordUrssaf: null,
      typeCompteUrssaf: compte.typeCompteUrssaf,
      nomUtilisateurImpotsGouv: compte.nomUtilisateurImpotsGouv,
      passwordImpotsGouv: null,
      nomUtilisateurProvigis: compte.nomUtilisateurProvigis,
      passwordProvigis: null,
      nomUtilisateurIdenum: compte.nomUtilisateurIdenum,
      passwordIdenum: null,
      passwordProbtp: null,
      nomUtilisateurCibtp: compte.nomUtilisateurCibtp,
      passwordCibtp: null,
    });
    this.buildDocumentStatuses(compte);
  }

  buildDocumentStatuses(compte: ICompteAdministration) {
    const c = compte.company;
    this.documentStatuses.set([
      {
        name: 'Attestation de vigilance URSSAF',
        type: 'URSSAF',
        source: 'URSSAF',
        available: c.idAttestationVigilanceUrssaf != null,
        id: c.idAttestationVigilanceUrssaf,
        date: c.dateAttestationVigilanceUrssaf,
        credentialsConfigured: compte.mdpSecretUrssaf != null,
        credentialSource: 'URSSAF',
      },
      {
        name: 'Extrait KBIS',
        type: 'MONIDENUM',
        source: 'MonIdenum',
        available: c.idKbis != null,
        id: c.idKbis,
        date: c.dateKbis,
        credentialsConfigured: compte.mdpSecretIdenum != null,
        credentialSource: 'MonIdenum',
      },
      {
        name: 'Attestation de régularité fiscale',
        type: 'IMPOTS_GOUV',
        source: 'Impots.gouv',
        available: c.idAttestationRegulariteFiscale != null,
        id: c.idAttestationRegulariteFiscale,
        date: c.dateAttestationRegulariteFiscale,
        credentialsConfigured: compte.mdpSecretImpotsGouv != null,
        credentialSource: 'Impots.gouv',
      },
      {
        name: 'Avis de CFE',
        type: 'IMPOTS_GOUV',
        source: 'Impots.gouv',
        available: c.idAvisCfe != null,
        id: c.idAvisCfe,
        date: c.dateAvisCfe,
        credentialsConfigured: compte.mdpSecretImpotsGouv != null,
        credentialSource: 'Impots.gouv',
        unavailableMessage: "Récupération lors de l'émission des avis CFE",
      },
    ]);
  }

  updatePasswordField(field: keyof IPasswordUpdateRequest, value: string) {
    this.passwordForm.update(form => ({...form, [field]: value || null}));
  }

  savePasswords() {
    this.savingPasswords.set(true);
    this.companyService.updatePasswords(this.currentSiret, this.passwordForm()).subscribe({
      next: () => {
        this.companyData.refresh(this.currentSiret).subscribe({
          next: c => {
            this.applyCompte(c);
            this.savingPasswords.set(false);
            this.notificationService.success('Identifiants mis à jour avec succès', 3000);
            this.passwordForm.update(form => ({
              ...form,
              passwordUrssaf: null,
              passwordImpotsGouv: null,
              passwordProvigis: null,
              passwordIdenum: null,
            }));
          },
          error: () => this.savingPasswords.set(false),
        });
      },
      error: () => {
        this.savingPasswords.set(false);
        this.notificationService.error('Erreur lors de la mise à jour des identifiants', 3000);
      },
    });
  }

  downloadDocument(doc: DocumentStatus) {
    if (!doc.id) {
      return;
    }
    this.companyService.downloadDocument(doc.id).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${doc.name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.notificationService.success(`Téléchargement de "${doc.name}" en cours`, 2000);
      },
      error: () => this.notificationService.error('Erreur lors du téléchargement du document', 3000),
    });
  }

  deleteCredentials(service: 'urssaf' | 'impots' | 'provigis' | 'idenum') {
    const labels: Record<string, string> = {
      urssaf: 'URSSAF',
      impots: 'Impots.gouv',
      provigis: 'Provigis',
      idenum: 'MonIdenum',
    };
    if (!confirm(`Supprimer les identifiants ${labels[service]} ?`)) {
      return;
    }
    this.companyService.deleteCredentials(this.currentSiret, service).subscribe({
      next: () => {
        this.companyData.refresh(this.currentSiret).subscribe(c => {
          this.applyCompte(c);
          this.notificationService.success(`Identifiants ${labels[service]} supprimés`, 3000);
        });
      },
      error: () => this.notificationService.error('Erreur lors de la suppression des identifiants', 3000),
    });
  }

  hasConfiguredCredentials(service: 'urssaf' | 'impots' | 'provigis' | 'idenum'): boolean {
    const compte = this.compteAdministration();
    if (!compte) {
      return false;
    }
    switch (service) {
      case 'urssaf':
        return compte.mdpSecretUrssaf != null;
      case 'impots':
        return compte.mdpSecretImpotsGouv != null;
      case 'provigis':
        return compte.mdpSecretProvigis != null;
      case 'idenum':
        return compte.mdpSecretIdenum != null;
    }
  }
}
